import {
  ChakraFlex,
  Tag,
  TagColor,
} from '@equip.health/ui';
import SelectableUserAvatarBlock, {
  AvatarUser,
} from '../SelectableUserAvatarBlock';
import { SELECTED_BADGE_TEXT } from '../constants';

/**
 * Only the provider fields ProviderCard needs to render. Role is
 * resolved by the caller (which knows the assignment context, e.g. multi-role
 * providers or providerType-specific display rules) rather than inferred here.
 */
export interface ConfirmationProvider {
  userId: string;
  firstName: string;
  lastName: string;
  chosenName?: string | null;
  pronouns?: string | string[];
  avatar?: { url?: string; thumbnailUrl?: string } | null;
  credentialsDetails?: { code?: string; name?: string }[];
  role: string;
}

export interface ProviderCardProps {
  provider: ConfirmationProvider;
}

const getCredentialsDisplay = (
  credentials?: string[] | { name?: string }[],
): string => {
  if (!credentials?.length) return '';
  if (typeof credentials[0] === 'string') {
    return (credentials as string[]).join(', ');
  }
  return (credentials as { name?: string }[])
    .map((c) => c.name)
    .filter(Boolean)
    .join(', ');
};

const ProviderCard = ({
  provider,
}: ProviderCardProps) => {
  const avatarUser: AvatarUser = {
    userId: provider.userId,
    firstName: provider.firstName,
    lastName: provider.lastName,
    chosenName: provider.chosenName ?? undefined,
    pronouns: provider.pronouns,
    role: 'provider',
    providerTypeName: provider.role,
    avatar: provider.avatar,
    avatarThumbnailUrl: provider.avatar?.thumbnailUrl,
    avatarUrl: provider.avatar?.url,
    credentialsDetails: provider.credentialsDetails,
  };

  const roleDisplay = [provider.role, getCredentialsDisplay(provider.credentialsDetails)]
    .filter(Boolean)
    .join(', ');

  return (
    <ChakraFlex
      alignItems="center"
      borderColor="neutral.line"
      borderRadius="12px"
      borderWidth="1px"
      gap="12px"
      justifyContent="space-between"
      padding="6px 18px 6px 6px"
    >
      <ChakraFlex flex="1" minWidth={0} overflow="hidden">
        <SelectableUserAvatarBlock
          avatarSize="sm"
          id={`selected-provider-${provider.userId}`}
          roleDisplay={roleDisplay}
          showPrimaryTag={false}
          showPronunciation={false}
          showPronouns
          showTimezone={false}
          user={avatarUser}
          variant="compact"
        />
      </ChakraFlex>
      <ChakraFlex flexShrink={0}>
        <Tag
          color={TagColor.pink}
          id={`selected-provider-badge-${provider.userId}`}
          value={SELECTED_BADGE_TEXT}
        />
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default ProviderCard;
